import { useCallback, useEffect, useMemo, useState } from "react";

import ResponsiveTabContainer from "@/Components/DocPage/new-doc-page/ResponsiveTabContainer";
import useTabsData from "@/Components/DocPage/new-doc-page/hooks/useGetTabsData";

import { getFaxDocuments } from "./api/faxApi";

const getDocumentSlotLabel = (documentSlot) => {
  if (!documentSlot) return "";
  const slotNumber = documentSlot.slot_number;
  const slotName = documentSlot.slot_name;
  const hasNumber = slotNumber !== null && slotNumber !== undefined && slotNumber !== "";

  if (!hasNumber && !slotName) return "";
  if (Number(slotNumber) === 0) return [slotName, "Generally"].filter(Boolean).join(" ");
  if (hasNumber && !slotName) return `${slotNumber}. Empty`;
  return hasNumber ? `${slotNumber}. ${slotName}` : slotName;
};

const FaxDocumentPicker = ({ caseId, selectedIds, onSelectionChange }) => {
  const [documents, setDocuments] = useState([]);
  const [knownDocuments, setKnownDocuments] = useState({});
  const [selectedPage, setSelectedPage] = useState("all");
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState("");
  const { pageSlots, allCount } = useTabsData();

  const loadDocuments = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError("");
    try {
      const response = await getFaxDocuments({ caseId, pageId: selectedPage });
      setDocuments(response);
      setKnownDocuments((current) => {
        const next = { ...current };
        response.forEach((document) => {
          next[document.id] = document;
        });
        return next;
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [caseId, selectedPage]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const selectedDocuments = useMemo(
    () => selectedIds.map((id) => knownDocuments[id] || { id, file_name: `Document ${id}` }),
    [knownDocuments, selectedIds]
  );
  const pageIconByName = useMemo(
    () => new Map(pageSlots.map((page) => [page.name, page.page_icon])),
    [pageSlots]
  );

  const toggleDocument = (document) => {
    if (selectedIds.includes(document.id)) {
      onSelectionChange(selectedIds.filter((id) => id !== document.id));
      return;
    }
    setKnownDocuments((current) => ({ ...current, [document.id]: document }));
    onSelectionChange([...selectedIds, document.id]);
  };

  const moveDocument = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= selectedIds.length) return;
    const reordered = [...selectedIds];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    onSelectionChange(reordered);
  };

  return (
    <div className="fax-document-picker">
      <div className="fax-selected-documents">
        <div className="fax-selected-documents-heading">
          <div>
            <strong>Fax order</strong>
            <span>Documents are merged in this order after the cover sheet.</span>
          </div>
          <span className="fax-selection-count">{selectedIds.length} selected</span>
        </div>
        <div className="fax-selected-document-list">
          {selectedDocuments.length ? (
            selectedDocuments.map((document, index) => (
              <div className="fax-selected-document" key={document.id}>
                <span className="fax-document-order">{index + 1}</span>
                <i className="ic ic-15 ic-file-colored" aria-hidden="true" />
                <strong title={document.file_name}>
                  {document.file_name || "Untitled document"}
                </strong>
                <div className="fax-selected-document-actions">
                  <button
                    type="button"
                    disabled={index === 0}
                    aria-label={`Move ${document.file_name || "document"} earlier`}
                    onClick={() => moveDocument(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={index === selectedDocuments.length - 1}
                    aria-label={`Move ${document.file_name || "document"} later`}
                    onClick={() => moveDocument(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="is-remove"
                    aria-label={`Remove ${document.file_name || "document"}`}
                    onClick={() => toggleDocument(document)}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="fax-selected-empty">Select documents below to build the fax.</div>
          )}
        </div>
      </div>

      <div className="fax-document-library">
        <div className="assemble-page-tabs-shell pt-0 fax-document-tabs">
          <ResponsiveTabContainer
            categoriesOnly
            onSelect={setSelectedPage}
            data={pageSlots}
            allCount={allCount}
            activeTab={selectedPage}
          />
        </div>

        <div className="table-responsive table--no-card rounded-0 border-0 fax-document-table-wrap">
          <table className="table table-borderless table-striped table-earning has-height-25 generate-doc-table fax-document-table">
            <thead>
              <tr>
                <th
                  className=""
                  style={{
                    minWidth: "25px",
                    maxWidth: "25px",
                    width: "25px",
                  }}
                >
                  {/*  */}
                </th>
                <th
                  style={{
                    minWidth: "25px",
                    maxWidth: "25px",
                    width: "25px",
                  }}
                  className="fax-check-column"
                />
                <th className="td-autosize">Page</th>
                <th />
                <th className="td-autosize">Doc Slot</th>
                <th />
                <th className="td-autosize">Name</th>
                <th />
                <th className="text-center td-autosize">Pages</th>
                <th
                  className=""
                  style={{
                    minWidth: "10px",
                    maxWidth: "10px",
                    width: "10px",
                  }}
                >
                  {/*  */}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="fax-document-table-state">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    Loading case documents…
                  </td>
                </tr>
              ) : documents.length ? (
                documents.map((document) => {
                  const selectionIndex = selectedIds.indexOf(document.id);
                  const selected = selectionIndex !== -1;
                  const pageIcon =
                    document?.page?.page_icon ||
                    document?.page_icon ||
                    pageIconByName.get(document.page_name);

                  return (
                    <tr
                      key={document.id}
                      className={selected ? "fax-document-selected-row" : ""}
                      onClick={() => toggleDocument(document)}
                    >
                      <td
                        className=""
                        style={{
                          color: "var(--primary-25)",
                          fontWeight: "600",
                        }}
                      >
                        {selected ? (
                          <span
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            className=""
                          >
                            {selectionIndex + 1}
                          </span>
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="fax-check-column">
                        <input
                          type="checkbox"
                          className="form-check template-checkbox mb-0"
                          checked={selected}
                          aria-label={`Select ${document.file_name || "document"}`}
                          onChange={() => toggleDocument(document)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </td>
                      <td className="text-nowrap text-start">
                        <div className="assemble-page-cell">
                          {pageIcon && (
                            <img src={pageIcon} className="assemble-page-cell-icon" alt="" />
                          )}
                          <span
                            style={{
                              color: "black",
                              fontWeight: "600",
                            }}
                          >
                            {document.page_name || document?.page?.name || ""}
                          </span>
                        </div>
                      </td>
                      <td />
                      <td
                        style={{
                          color: "black",
                          fontWeight: "600",
                        }}
                        className="text-nowrap text-start"
                      >
                        {getDocumentSlotLabel(document.document_slot)}
                      </td>
                      <td />
                      <td
                        style={{
                          color: "black",
                          fontWeight: "600",
                        }}
                        className="fax-document-name"
                        title={document.file_name}
                      >
                        {document.file_name || ""}
                      </td>
                      <td />
                      <td
                        style={{
                          color: "black",
                          fontWeight: "600",
                        }}
                        className="text-center"
                      >
                        {document.page_count || ""}
                      </td>

                      <td />
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="fax-document-table-state">
                    No documents were found in this category.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FaxDocumentPicker;
